import { useEffect, useRef, useState } from 'react'
import Icon from './Icon'

/**
 * Where a hero image sits in its frame, and how far it is zoomed.
 *
 * A poster is composed for a portrait crop and a press shot for a landscape
 * one; a hero is neither. Rather than guess, this holds whatever the user
 * dragged it to — an offset as a fraction of the frame, so it survives being
 * rendered at a different width on a different screen.
 */
export interface HeroFraming {
  scale: number
  offsetX: number
  offsetY: number
}

export const IDENTITY_FRAMING: HeroFraming = { scale: 1, offsetX: 0, offsetY: 0 }

/**
 * The tour's identity, on its own artwork.
 *
 * The poster is the tour — a plain title over a panel throws that away. The
 * text sits ON the image, so it stays white in every skin: this surface is
 * dark regardless of Showtime or Daylight, and `--text-primary` would vanish
 * on it in light mode.
 *
 * When there's no artwork the frame keeps its shape rather than collapsing, so
 * the page doesn't jump between tours.
 */
export default function TourHero({
  artist,
  tourName,
  posterURL,
  canEdit,
  onEdit,
  /** Rendered under the title — a stat strip, a status chip, anything that
   *  belongs to the masthead rather than to the page below it. */
  footer,
  /** Sits beside the edit button on the artwork. Share lives here, so it is
   *  offered to viewers, who never see an edit button at all. */
  actions,
  editLabel = 'Edit tour',
  framing,
  onSaveFraming,
}: {
  artist: string
  tourName: string
  posterURL?: string | null
  canEdit: boolean
  onEdit: () => void
  footer?: React.ReactNode
  actions?: React.ReactNode
  editLabel?: string
  /** Current crop. Omit to leave the image centred and un-repositionable. */
  framing?: HeroFraming
  /** Persists a new crop. Its presence is what offers the control. */
  onSaveFraming?: (framing: HeroFraming) => Promise<void> | void
}) {
  const [repositioning, setRepositioning] = useState(false)
  const [draft, setDraft] = useState<HeroFraming>(framing ?? IDENTITY_FRAMING)
  const [saving, setSaving] = useState(false)
  const frame = useRef<HTMLDivElement | null>(null)
  const drag = useRef<{ x: number; y: number; from: HeroFraming } | null>(null)
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null)
  const [box, setBox] = useState<{ w: number; h: number } | null>(null)

  // The offsets are fractions of the frame, so the frame's pixel size is
  // needed to render them. Watched rather than measured once: the hero is
  // full-bleed, so it changes with the window.
  useEffect(() => {
    const node = frame.current
    if (!node) return
    const measure = () => setBox({ w: node.clientWidth, h: node.clientHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!repositioning) setDraft(framing ?? IDENTITY_FRAMING)
  }, [framing, repositioning])

  const applied = repositioning ? draft : (framing ?? IDENTITY_FRAMING)
  const portrait = Boolean(natural && natural.h > natural.w * 1.05)

  /**
   * The image at the size `cover` would draw it, in pixels.
   *
   * Rendering it at this size and panning it is the whole trick.
   * `object-fit: cover` crops to the frame *first*, and a transform then
   * slides that crop around — so dragging up moved the visible band but never
   * revealed anything above or below it, because everything outside the
   * original crop had already been thrown away. Sizing the element to the full
   * cover dimensions means the rest of the picture is actually there to pan
   * into.
   */
  const cover =
    natural && box && natural.w > 0 && natural.h > 0
      ? Math.max(box.w / natural.w, box.h / natural.h)
      : null
  const coverWidth = cover && natural ? natural.w * cover : null
  const coverHeight = cover && natural ? natural.h * cover : null

  const slackY =
    coverHeight && box ? Math.max(0, (coverHeight * applied.scale - box.h) / 2) / box.h : 0

  // Portrait artwork starts near the top rather than centred: the middle of a
  // press shot in a wide hero is everybody's torso, and a poster puts its name
  // at the top. Only when nothing has been positioned by hand.
  const untouched =
    applied.scale === 1 && applied.offsetX === 0 && applied.offsetY === 0
  const offsetY =
    untouched && portrait && !repositioning ? slackY * 0.64 : applied.offsetY
  const canReposition = Boolean(onSaveFraming && posterURL && canEdit)

  /**
   * Keeps the image covering the frame.
   *
   * Without this you can drag a wide shot far enough that the panel shows
   * through down one side — the artwork stops being a background and becomes a
   * floating rectangle.
   *
   * The slack is real overflow, measured, not derived from the zoom. An
   * earlier version used `(scale - 1) / 2`, which says there is nothing to pan
   * at scale 1 — but `object-fit: cover` has *already* cropped the image to
   * fill the frame, so a tall poster in a wide hero has most of its height
   * hidden and nothing would move until you zoomed in first.
   */
  const clamp = (next: HeroFraming): HeroFraming => {
    const rect = frame.current?.getBoundingClientRect()
    let limitX = Math.max(0, (next.scale - 1) / 2)
    let limitY = limitX

    if (rect && natural && natural.w > 0 && natural.h > 0) {
      const fit = Math.max(rect.width / natural.w, rect.height / natural.h)
      const renderedWidth = natural.w * fit * next.scale
      const renderedHeight = natural.h * fit * next.scale
      limitX = Math.max(0, (renderedWidth - rect.width) / 2) / rect.width
      limitY = Math.max(0, (renderedHeight - rect.height) / 2) / rect.height
    }

    return {
      scale: next.scale,
      offsetX: Math.min(Math.max(next.offsetX, -limitX), limitX),
      offsetY: Math.min(Math.max(next.offsetY, -limitY), limitY),
    }
  }

  const onPointerDown = (event: React.PointerEvent) => {
    if (!repositioning) return

    // The controls sit inside the frame, and the frame is what listens for
    // drags. Capturing the pointer here swallowed the press before it reached
    // Cancel or Done, so they looked dead — every click was read as the start
    // of a drag on the artwork behind them.
    if ((event.target as HTMLElement).closest('button')) return

    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { x: event.clientX, y: event.clientY, from: draft }
  }

  const onPointerMove = (event: React.PointerEvent) => {
    if (!repositioning || !drag.current) return
    const box = frame.current?.getBoundingClientRect()
    if (!box) return
    setDraft(
      clamp({
        ...drag.current.from,
        offsetX: drag.current.from.offsetX + (event.clientX - drag.current.x) / box.width,
        offsetY: drag.current.from.offsetY + (event.clientY - drag.current.y) / box.height,
      }),
    )
  }

  const onPointerUp = () => {
    drag.current = null
  }

  const onWheel = (event: React.WheelEvent) => {
    if (!repositioning) return
    event.preventDefault()
    // Never below 1: under it the image stops covering the frame.
    const scale = Math.min(Math.max(draft.scale - event.deltaY * 0.0015, 1), 4)
    setDraft(clamp({ ...draft, scale }))
  }

  const save = async () => {
    if (!onSaveFraming) return
    setSaving(true)
    try {
      await onSaveFraming(draft)
      setRepositioning(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        // Full-bleed: the artwork runs to the window edge and up under the top
        // of the pane. An inset banner with a hairline reads as a widget; a
        // tour poster should read as the masthead of the page.
        //
        // Height is set directly rather than via `aspect-ratio`. A ratio with a
        // `max-height` doesn't clamp the height and keep the width — it shrinks
        // the *width* to preserve the ratio, which left a gap down the right
        // edge on any pane wider than maxHeight × ratio.
        width: '100%',
        // Taller for portrait artwork.
        //
        // A 3050×4066 press shot in a 3.3:1 letterbox can only ever show a
        // thin horizontal band of itself — repositioning picks *which* band,
        // it can't make one fit. Posters and press shots are usually portrait,
        // so the default frame was fighting the artwork it exists to show.
        //
        // A landscape image keeps the shorter frame: it fills that happily,
        // and a wide masthead is the right shape when the artwork is wide.
        height: portrait
          ? 'clamp(300px, 34vw, 520px)'
          : 'clamp(220px, 24vw, 380px)',
        // Only once the natural size is known, so the frame doesn't visibly
        // jump on every load.
        transition: natural ? 'height 180ms ease-out' : undefined,
        background: posterURL
          ? 'var(--panel-raised)'
          : 'linear-gradient(120deg, var(--accent-wash), var(--panel-raised))',
        cursor: repositioning ? (drag.current ? 'grabbing' : 'grab') : undefined,
        touchAction: repositioning ? 'none' : undefined,
      }}
      ref={frame}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
    >
      {posterURL && (
        <img
          src={posterURL}
          alt=""
          style={
            coverWidth && coverHeight && box
              ? {
                  position: 'absolute',
                  // Anchored to the frame's centre and sized to the full cover
                  // dimensions, so the whole picture is present and panning
                  // reveals the parts outside the frame. `object-fit: cover`
                  // cannot do this: it crops to the frame first, and a
                  // transform then only slides the surviving crop — which is
                  // why dragging up moved the band without ever showing what
                  // was above it.
                  left: '50%',
                  top: '50%',
                  width: coverWidth,
                  height: coverHeight,
                  maxWidth: 'none',
                  transform:
                    `translate(-50%, -50%)` +
                    ` translate(${applied.offsetX * box.w}px, ${offsetY * box.h}px)` +
                    ` scale(${applied.scale})`,
                  // Instant while dragging — an eased transform lags the
                  // pointer and makes the image feel like it is resisting.
                  transition: drag.current ? 'none' : 'transform 120ms ease-out',
                  userSelect: 'none',
                }
              : {
                  // Until the natural size is known there is nothing to
                  // compute a cover box from, so fall back to plain cover.
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: portrait ? '50% 18%' : '50% 50%',
                  userSelect: 'none',
                }
          }
          draggable={false}
          onLoad={(event) => {
            const image = event.currentTarget
            setNatural({ w: image.naturalWidth, h: image.naturalHeight })
          }}
        />
      )}

      {/* Legibility scrim, weighted to the lower-left where the type sits, so
          it darkens the words without flattening the whole image. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to right, rgb(0 0 0 / 0.78) 0%, rgb(0 0 0 / 0.45) 42%, rgb(0 0 0 / 0.12) 72%, transparent 100%),' +
            'linear-gradient(to top, rgb(0 0 0 / 0.55) 0%, transparent 55%)',
        }}
      />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          // Matches the page gutter so the tour name lines up with the stat
          // cards below it rather than floating at its own margin.
          padding: 'clamp(22px, 3vw, 34px) var(--gutter)',
        }}
      >
        <span
          className="t-eyebrow"
          style={{ color: 'rgb(255 255 255 / 0.78)', marginBottom: 6 }}
        >
          {artist}
        </span>
        <h1
          className="t-display-xl"
          style={{
            color: '#fff',
            margin: 0,
            textShadow: '0 2px 18px rgb(0 0 0 / 0.45)',
            maxWidth: '80%',
          }}
        >
          {tourName}
        </h1>
        {footer && <div style={{ marginTop: 12 }}>{footer}</div>}
      </div>

      <div
        className="row"
        style={{ position: 'absolute', top: 18, right: 'var(--gutter)', gap: 8 }}
      >
        {repositioning ? (
          <>
            <span
              style={{
                padding: '9px 14px',
                borderRadius: 'var(--radius-control)',
                background: 'rgb(0 0 0 / 0.55)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              Drag to move · scroll to zoom
            </span>
            <button
              onClick={() => {
                setDraft(framing ?? IDENTITY_FRAMING)
                setRepositioning(false)
              }}
              style={{
                padding: '9px 15px',
                borderRadius: 'var(--radius-control)',
                background: 'rgb(0 0 0 / 0.55)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                fontSize: '0.84rem',
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => void save()}
              disabled={saving}
              style={{
                padding: '9px 15px',
                borderRadius: 'var(--radius-control)',
                background: 'var(--accent)',
                color: '#fff',
                fontSize: '0.84rem',
                fontWeight: 700,
              }}
            >
              {saving ? 'Saving…' : 'Done'}
            </button>
          </>
        ) : (
          <>
            {actions}
            {canReposition && (
              <button
                onClick={() => setRepositioning(true)}
                className="row"
                title="Reposition the artwork"
                style={{
                  gap: 7,
                  padding: '9px 15px',
                  borderRadius: 'var(--radius-control)',
                  background: 'rgb(0 0 0 / 0.55)',
                  backdropFilter: 'blur(8px)',
                  color: '#fff',
                  fontSize: '0.84rem',
                  fontWeight: 600,
                }}
              >
                Reposition
              </button>
            )}
            {canEdit && (
        <button
          onClick={onEdit}
          className="row"
          style={{
            gap: 7,
            padding: '9px 15px',
            borderRadius: 'var(--radius-control)',
            // Its own dark surface, not a theme token — it sits on artwork.
            background: 'rgb(0 0 0 / 0.55)',
            backdropFilter: 'blur(8px)',
            color: '#fff',
            fontSize: '0.84rem',
            fontWeight: 600,
          }}
        >
          {editLabel}
          <Icon name="edit" size={15} />
        </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/** The on-artwork button style, shared so Share and Edit match exactly. */
export const heroButtonStyle: React.CSSProperties = {
  gap: 7,
  padding: '9px 15px',
  borderRadius: 'var(--radius-control)',
  background: 'rgb(0 0 0 / 0.55)',
  backdropFilter: 'blur(8px)',
  color: '#fff',
  fontSize: '0.84rem',
  fontWeight: 600,
}
